// --- React
import { useCallback, useMemo, useState } from 'react';
// --- Utils
import { filterCriteria } from 'datastage/utils/filterCriteria';
// --- Config
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';

/**
 * Custom hook for Job Tasks grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Job Tasks grid
 * - Fetches sorted job tasks data from the API
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Dependencies and handlers for the grid
 * @returns {object} { setSort, columns }
 */
export function useJobTasksGridUtils({
    datastageApi,
    filterForm,
    defaultFilterRange,
    updateJobTasks,
    handlers,
}) {
    const [sortedColId, setSortedColId] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    // Handle sorting: update state, fetch sorted data, update Job Tasks
    const setSort = useCallback(
        async (colId, direction) => {
            setSortedColId(colId);
            setSortDirection(direction);
            handlers.setLoadingTrue();
            try {
                const data = await datastageApi.jobTasks(
                    filterCriteria(filterForm, '', 'jobTasks'),
                    defaultFilterRange,
                    [colId, direction.toUpperCase()],
                );
                updateJobTasks({
                    tasks: data?.content,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable?.pageNumber,
                    sortParam: [colId, direction.toUpperCase()],
                });
            } catch (error) {
                console.error('Error sorting job tasks:', error);
            } finally {
                handlers.setLoadingFalse();
            }
        },
        [filterForm, defaultFilterRange, updateJobTasks, handlers],
    );

    // Prepare column definitions with sorting controls
    const baseColumns = columnDefs['jobTasks'](...handlers.columnHandlers)['default'];

    const columns = useMemo(
        () =>
            baseColumns.map((col) => ({
                ...col,
                headerComponentParams: {
                    ...col.headerComponentParams,
                    setSort,
                    sortedColId,
                    sortDirection,
                },
            })),
        [setSort, sortedColId, sortDirection, baseColumns],
    );

    return { setSort, columns };
}
